import { Box, Checkbox, debounce, Skeleton, useTheme } from '@mui/material';
import { EntityBase } from '@provision/entity-types';
import type { VirtualItem } from '@tanstack/react-virtual';
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_ColumnSizingState,
  MRT_RowVirtualizer,
  MRT_TableOptions,
} from 'material-react-table';
import {
  memo,
  MouseEvent,
  ReactElement,
  UIEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { pathOr, stringToPath } from 'remeda';
import {
  CustomViewAttribute,
  CustomViewEntity,
  UIType,
} from 'src/graphql/entities/custom-view';
import {
  CustomAttributeColumnRenderer,
  CustomAttributesRow,
} from 'src/graphql/entities/custom-view/custom-view-config/custom-attributes-bridge';
import { GridDensity } from 'src/store/custom-views';
import {
  useMaterialReactTableWithShortcuts,
  useScrollFallbackWhenNotOverflowing,
} from 'src/utils/hooks';
import { customComponentsMap } from './custom-components-map';
import { generalComponentsMap } from './general-components-map/general-components-map';

export interface TableSelectionState {
  isRowSelected: (id: string) => boolean;
  onRowSelectionChange: (id: string, selected: boolean) => void;
  onToggleSelectAllVisible: (selected: boolean) => void;
  isAllVisibleSelected: boolean;
  isSomeVisibleSelected: boolean;
}

const SKELETON_ROW_COUNT = 25;

const DEFAULT_ROWS: EntityBase[] = [];

const CHECKBOX_COLUMN_ID = '__bulk_select__';

const CustomViewsEntitiesTableComp = <T extends EntityBase>({
  rows = DEFAULT_ROWS as T[],
  entityName,
  selectedColumns,
  onClick,
  totalRecords,
  selectedId,
  hideHeaders,
  onScroll,
  isLoading,
  isLoadingMore,
  setColumnWidth,
  columnsWidthMap,
  onSortChange,
  sort,
  gridDensity,
  selectionState,
  customAttributeRenderers,
  hasDetachedPanel,
  onRecordOpenFullPage,
}: {
  gridDensity?: GridDensity;
  rows?: T[];
  entityName: CustomViewEntity;
  selectedColumns?: CustomViewAttribute[] | null;
  customAttributeRenderers?: Map<string, CustomAttributeColumnRenderer>;
  onClick?: (id: T['id'], record: T, event?: MouseEvent) => void;
  totalRecords: number;
  selectedId?: string;
  hideHeaders?: boolean;
  onScroll?: (e: UIEvent) => unknown;
  isLoading?: boolean;
  isLoadingMore?: boolean;
  setColumnWidth?: (column: string, width: number) => void;
  columnsWidthMap?: Record<string, number>;
  onSortChange?: (sort: { id: string; desc: boolean }) => void;
  sort?: { id: string; desc: boolean } | null;
  selectionState?: TableSelectionState;
  /** Enables ↑/↓ retargeting the side panel and Enter promoting to full page; see use-mrt-table-with-shortcuts. */
  hasDetachedPanel?: boolean;
  onRecordOpenFullPage?: (id: T['id'], record: T) => void;
}) => {
  const theme = useTheme();

  const customComponentsByAttribute = customComponentsMap[entityName];

  const skeletonRowCount = isLoadingMore ? SKELETON_ROW_COUNT : 0;

  const tableData: T[] = useMemo(
    () =>
      skeletonRowCount > 0
        ? [
            ...rows,
            ...Array.from({ length: skeletonRowCount }).map(
              () => ({ id: 'skeleton' }) as unknown as T,
            ),
          ]
        : rows,
    [rows, skeletonRowCount],
  );

  const columns = useMemo<MRT_ColumnDef<T>[]>(() => {
    if (!selectedColumns?.length) {
      return [];
    }

    const dataColumns = selectedColumns.map<MRT_ColumnDef<T>>((column) => {
      const { attribute, accessor, sortingKey } = column;
      // Present only for custom-attribute columns (and only when the feature is on); when undefined the
      // built-in accessor/cell path below is used verbatim.
      const customAttributeRenderer = customAttributeRenderers?.get(attribute);

      return {
        grow: false,
        id: attribute,
        enableSorting: !!onSortChange && !!sortingKey,
        // u00A0 is a non-breaking space, it is used to avoid extra line appearing when there is not header
        header: !column.hideHeader ? column.attributeLabel : '\u00A0',
        // using accessorFn instead of accessor to avoid warnings for empty values for
        // deeply nested values, there is no other way to bypass or fix this,
        // custom implementation the only way which worked
        accessorFn: customAttributeRenderer
          ? // custom attribute values are decoded from the row's customAttributes blob, not a nested path
            (data: T) => customAttributeRenderer.getStringValue(data as CustomAttributesRow)
          : (data: T) => {
              const attributePath = stringToPath(accessor);

              // @ts-expect-error Incorrectly derived type for nested properties path.
              return pathOr(data, attributePath, '');
            },
        minSize: 1, // 1V-3254: let users have column size as small as they want it to be
        size: columnsWidthMap?.[accessor] ?? 100,
        Cell: ({ row, renderedCellValue }) => {
          if (row.original.id === 'skeleton') {
            return (
              <Skeleton
                variant="rectangular"
                width="60%"
                sx={{ borderRadius: '6px' }}
                height="1rem"
              />
            );
          }

          // custom attribute cells decode the row's customAttributes blob via the field-view registry.
          if (customAttributeRenderer) {
            return customAttributeRenderer.renderCell(row.original as CustomAttributesRow);
          }

          // TODO need to improve components typings so it can identify types
          // by itself without asserting it via "as"

          const renderComponent = generalComponentsMap[column.uiType as UIType.Text];
          const attrKey = column.attribute;

          const customComponent = customComponentsByAttribute?.[attrKey];

          const mappedValue =
            customComponent?.(renderedCellValue as string, row.original) ?? renderedCellValue;

          return renderComponent?.(mappedValue as string, column) ?? mappedValue;
        },
      };
    });

    if (!selectionState) {
      return dataColumns;
    }

    const checkboxColumn: MRT_ColumnDef<T> = {
      id: CHECKBOX_COLUMN_ID,
      columnDefType: 'display',
      header: '\u00A0',
      enableSorting: false,
      enableColumnActions: false,
      enableResizing: false,
      size: 48,
      minSize: 48,
      maxSize: 48,
      grow: false,
      Header: () => {
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              minHeight: '2rem',
            }}
          >
            <Checkbox
              size="small"
              checked={selectionState.isAllVisibleSelected}
              indeterminate={Boolean(
                selectionState.isSomeVisibleSelected && !selectionState.isAllVisibleSelected,
              )}
              onChange={(_e, checked) => selectionState.onToggleSelectAllVisible(checked)}
              onClick={(e) => {
                e.stopPropagation();
                (e.target as HTMLElement).blur();
              }}
              sx={{
                padding: '0.25rem',
                opacity: selectionState.isSomeVisibleSelected ? 1 : 0,
                'thead:hover &': { opacity: selectionState.isSomeVisibleSelected ? 1 : 0 },
                transition: 'opacity 0.15s ease',
              }}
            />
          </Box>
        );
      },
      Cell: ({ row }) => {
        if (row.original.id === 'skeleton') return null;
        const id = String(row.original.id);
        const isSelected = selectionState.isRowSelected(id);
        return (
          <Checkbox
            size="small"
            checked={isSelected}
            data-testid={`custom-views-row-select-${id}`}
            onChange={(_e, checked) => selectionState.onRowSelectionChange(id, checked)}
            onClick={(e) => {
              e.stopPropagation();
              (e.target as HTMLElement).blur();
            }}
            sx={{
              padding: '0.25rem',
              opacity: isSelected ? 1 : 0,
              'tr:hover &': { opacity: 1 },
              transition: 'opacity 0.15s ease',
            }}
          />
        );
      },
    };

    return [checkboxColumn, ...dataColumns];
  }, [
    selectedColumns,
    onSortChange,
    customComponentsByAttribute,
    columnsWidthMap,
    selectionState,
    customAttributeRenderers,
  ]);

  const previousColumnSizingRef = useRef<Record<string, number>>({});

  const lastScrollTopRef = useRef(0);
  const rowVirtualizerInstanceRef = useRef<MRT_RowVirtualizer>(null);
  const measurementsCacheRef = useRef<VirtualItem[]>([]);

  const [allowUserSelection, setAllowUserSelection] = useState(true);

  useEffect(() => {
    // disable user selection when Shift key is pressed
    // to prevent text selection during Shift + [row click]
    // to open the row in a new tab
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') setAllowUserSelection(false);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') setAllowUserSelection(true);
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  const debouncedOnClick = useMemo(
    () =>
      debounce((id: T['id'], record: T, event?: MouseEvent) => {
        onClick?.(id, record, event);
      }, 300),
    [onClick],
  );

  const selectedIndex = useMemo(() => {
    if (!selectedId) {
      return -1;
    }

    return tableData.findIndex((row) => row.id === selectedId);
  }, [selectedId, tableData]);

  const tableDensity = useMemo(() => {
    if (gridDensity === 'standard') {
      return 'spacious';
    }
    return gridDensity || 'comfortable';
  }, [gridDensity]);

  const table = useMaterialReactTableWithShortcuts<T>(
    selectedIndex,
    {
      columns,
      enableRowVirtualization: true,
      enableColumnVirtualization: true,
      rowVirtualizerInstanceRef,
      data: tableData,
      rowCount: Math.min(totalRecords, tableData.length),
      enableTableHead: !hideHeaders,
      enableColumnResizing: !!setColumnWidth,
      enableStickyHeader: true,
      manualSorting: true,
      onSortingChange: onSortChange
        ? (updaterOrValue) => {
            const newSorting =
              typeof updaterOrValue === 'function'
                ? updaterOrValue(sort ? [sort] : [])
                : updaterOrValue;

            onSortChange?.(newSorting[0]);
            return newSorting;
          }
        : undefined,
      state: {
        showSkeletons: isLoading && rows.length === 0,
        sorting: sort ? [sort] : [],
        columnSizing: columnsWidthMap ?? {},
        density: tableDensity,
      },
      muiSkeletonProps: {
        animation: 'wave',
      },
      mrtTheme: {
        baseBackgroundColor: theme.palette.custom.OV.sys.surfaceContainerLowest,
      },
      muiTableHeadCellProps: {
        sx: (theme) => ({
          backgroundColor: theme.palette.custom?.OV?.sys?.surfaceContainerLowest || '#f0f0f0',
          '& .MuiTableHeadCell-Content': {
            whiteSpace: 'nowrap',
          },

          '& [data-testid="SyncAltIcon"].MuiTableSortLabel-icon': {
            visibility: 'hidden',
            opacity: 0,
          },

          '&:hover .MuiTableSortLabel-icon': {
            visibility: 'visible',
            opacity: 1,
          },
        }),
      },
      muiTableHeadProps: {
        sx: (theme) => ({
          backgroundColor: theme.palette.custom.OV.sys.surfaceContainerLowest,
        }),
      },
      enableTopToolbar: false,
      enableBottomToolbar: false,
      enableRowActions: false,
      enablePagination: false,
      muiTablePaperProps: {
        elevation: 0,
        sx: (theme) => ({
          backgroundColor: theme.palette.custom.OV.sys.surfaceContainerLowest,
          height: '100%',
        }),
      },
      defaultColumn: {
        minSize: 120,
        enableSorting: false,
        enableColumnActions: false,
      },
      rowVirtualizerOptions: {
        overscan: 4,
        initialOffset: () => lastScrollTopRef.current,
        initialMeasurementsCache: measurementsCacheRef.current,
      },
      muiTableBodyRowProps: ({ row }) => {
        const id = row.original?.id;

        return {
          onClick: (event: MouseEvent) => {
            event.persist();
            if (id !== 'skeleton') {
              debouncedOnClick(id, row.original, event);
            }
          },
          sx: {
            cursor: 'pointer',
            // `text`, not `auto`: index.css opts the whole touch UI out of selection,
            // and `auto` resolves to the ancestor's `none` instead of overriding it
            userSelect: allowUserSelection ? 'text' : 'none',
            backgroundColor:
              selectedId && id === selectedId
                ? theme.palette.custom.OV.sys.surfaceContainerHigh
                : theme.palette.custom.OV.sys.surfaceContainerLowest,
          },
        };
      },
      muiTableContainerProps: {
        onScroll: (e) => {
          lastScrollTopRef.current = e.currentTarget.scrollTop;
          const cache = rowVirtualizerInstanceRef.current?.measurementsCache;
          if (cache?.length) {
            measurementsCacheRef.current = cache;
          }
          onScroll?.(e);
        },
        sx: {
          backgroundColor: theme.palette.custom.OV.sys.surfaceContainerLowest,
          paddingBottom: '13rem',
          height: '100%',
        },
      },
      initialState: {
        columnPinning: {
          left: selectionState ? [CHECKBOX_COLUMN_ID] : [],
          right: ['mrt-row-actions'],
        },
      },
      onColumnSizingChange: (updaterOrValue) => {
        if (!setColumnWidth) {
          return;
        }

        let columnSizingState = updaterOrValue as MRT_ColumnSizingState;

        if (typeof updaterOrValue === 'function') {
          // if updaterOrValue is a function, call it with the previous state
          columnSizingState = updaterOrValue(table.getState().columnSizing);
        }

        const changedEntry = Object.entries(columnSizingState).find(
          ([columnId, width]) => previousColumnSizingRef.current[columnId] !== width,
        );

        if (changedEntry) {
          setColumnWidth(...changedEntry);
        }

        previousColumnSizingRef.current = columnSizingState;
      },
      muiTableBodyCellProps: ({ column, row }) => ({
        sx: (theme) => ({
          whiteSpace: 'nowrap',
          ...(column.id === 'siteHealth' ? { position: 'relative', overflow: 'visible' } : {}),
          ...(selectionState && column.id === CHECKBOX_COLUMN_ID
            ? {
                backgroundColor:
                  selectedId && row.original?.id === selectedId
                    ? theme.palette.custom.OV.sys.surfaceContainerHigh
                    : theme.palette.custom?.OV?.sys?.surfaceContainerLowest,
                backgroundImage: 'none',
              }
            : {}),
        }),
      }),
    } as MRT_TableOptions<T>,
    {
      onRowClick: debouncedOnClick,
      hasDetachedPanel,
      onRecordOpenFullPage,
    },
  );

  const previousColumnOrderRef = useRef<string[] | null>(null);

  useEffect(() => {
    const newColumnOrder = columns.map((i) => String(i.id));
    const previousColumnOrder = previousColumnOrderRef.current;

    if (previousColumnOrder?.join(',') !== newColumnOrder?.join(',')) {
      table.setColumnOrder(newColumnOrder);
      previousColumnOrderRef.current = newColumnOrder;
    }
  }, [columns, table]);

  useScrollFallbackWhenNotOverflowing({
    containerRef: table.refs.tableContainerRef,
    onScroll,
    dataKey: tableData.length,
  });

  return <MaterialReactTable table={table} />;
};

export const CustomViewsEntitiesTable = memo(CustomViewsEntitiesTableComp) as <
  T extends EntityBase,
>(
  props: React.ComponentProps<typeof CustomViewsEntitiesTableComp<T>>,
) => ReactElement;
